import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Activity,
  AlertOctagon,
  BarChart3,
  Inbox,
  List,
  ListChecks,
  Map as MapIcon,
  Timer,
} from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { BarChart } from '@/components/charts/BarChart';
import { DonutChart } from '@/components/charts/DonutChart';
import { palette } from '@/components/charts/chartTheme';
import { DataView } from '@/components/data/DataView';
import { OrderRiderCell } from '@/components/orders/OrderRiderCell';
import { OrdersMapView } from '@/components/maps/OrdersMapView';
import {
  useOrderCancellations,
  useOrderFunnel,
  useOrderSla,
  useOrderSlaContributors,
  useOrdersPoints,
} from '@/api/bi';
import { useOrdersList, type OrderRow } from '@/api/legacy/orders';
import { useFilters, rangeISO, type TimeRangeMode } from '@/state/filters';
import { fmt, TIMEZONE } from '@/lib/format';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { statusLabel, statusTone } from '@/lib/status';
import { formatInTimeZone } from 'date-fns-tz';
import { cn } from '@/lib/cn';
import { openRiderOrders } from '@/state/riderOrdersDialog';
import { SLA_TAG_LABELS } from '@/lib/slaTags';

const STAGE_DURATION_LABEL: Record<string, string> = {
  created_to_acknowledged: 'Created → Assigned',
  acknowledged_to_packed: 'Assigned → Packed',
  packed_to_billed: 'Packed → Billed',
  billed_to_picked: 'Billed → Picked',
  picked_to_completed: 'Picked → Completed',
};

const ALL_SLA_TAGS = Object.keys(SLA_TAG_LABELS);

export function OrdersPage() {
  const { range, storeId, storeIds, timeRangeMode } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const filters = { start: iso.start, end: iso.end, storeId, storeIds };

  const tab: 'analytics' | 'list' = searchParams.get('tab') === 'list' ? 'list' : 'analytics';
  const listStatus = searchParams.get('status') ?? '';
  const listTags = searchParams.get('tags') ?? '';
  const cancellationReason = searchParams.get('cancellation_reason') ?? '';
  const [cancellationPage, setCancellationPage] = useState(1);

  const funnel = useOrderFunnel(filters);
  const sla = useOrderSla(filters);
  const slaContributors = useOrderSlaContributors({ ...filters, granularity: 'day', limit: 3 });
  const cancellations = useOrderCancellations({
    ...filters,
    page: cancellationPage,
    pageSize: 10,
  });

  useEffect(() => {
    setCancellationPage(1);
  }, [iso.start, iso.end, storeId, storeIds]);

  const funnelChartData = useMemo(() => {
    return (funnel.data?.steps ?? []).map((s) => ({
      label: statusLabel(s.status),
      count: s.count,
      drop_pct: s.drop_pct,
    }));
  }, [funnel.data]);

  const slaDonut = useMemo(() => {
    const rows = sla.data?.breakdown ?? [];
    return rows.map((r) => ({ name: r.tag, value: r.count }));
  }, [sla.data]);

  function updateOrderParams(mutator: (next: URLSearchParams) => void) {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    setSearchParams(next);
  }

  function handleTabChange(nextTab: 'analytics' | 'list') {
    updateOrderParams((next) => {
      next.set('tab', nextTab);
    });
  }

  function drillToOrders(params: { status?: string; tags?: string; cancellationReason?: string }) {
    updateOrderParams((next) => {
      next.set('tab', 'list');
      next.delete('status');
      next.delete('tags');
      next.delete('cancellation_reason');
      if (params.status) next.set('status', params.status);
      if (params.tags) next.set('tags', params.tags);
      if (params.cancellationReason) next.set('cancellation_reason', params.cancellationReason);
    });
  }

  function clearDrilldown() {
    updateOrderParams((next) => {
      next.delete('tags');
      next.delete('cancellation_reason');
      if (next.get('status') === 'cancelled') next.delete('status');
    });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Orders"
        description="Funnel conversion, stage durations, SLA breaches, and the orders themselves — in one place."
        actions={
          <div className="flex items-center gap-1.5">
            <a
              href="/orders/sla-heatmap"
              className="press inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <AlertOctagon className="h-3.5 w-3.5" /> SLA heatmap
            </a>
            <a
              href="/orders/durations"
              className="press inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Timer className="h-3.5 w-3.5" /> Stage percentiles
            </a>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={(v) => handleTabChange(v as 'analytics' | 'list')} className="mt-4">
        <TabsList>
          <TabsTrigger value="analytics" className="gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" aria-hidden /> Analytics
          </TabsTrigger>
          <TabsTrigger value="list" className="gap-1.5">
            <ListChecks className="h-3.5 w-3.5" aria-hidden /> Orders
          </TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="mt-4 focus-visible:outline-none">
          <OrdersListTab
            from={ymd(range.from)}
            to={ymd(range.to)}
            startISO={iso.start}
            endISO={iso.end}
            timeRangeMode={timeRangeMode}
            storeId={storeId}
            storeIds={storeIds}
            statusParam={listStatus}
            tags={listTags}
            cancellationReason={cancellationReason}
            onClearDrilldown={clearDrilldown}
          />
        </TabsContent>
        <TabsContent value="analytics" className="mt-4 focus-visible:outline-none">
          <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Funnel</CardTitle>
            {funnel.data ? (
              <Badge tone="neutral">
                <AnimatedNumber value={funnel.data.steps[0]?.count ?? 0} duration={500} /> entered
              </Badge>
            ) : null}
          </CardHeader>
          <CardBody>
            {funnel.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : funnel.isError ? (
              <ErrorState onRetry={() => void funnel.refetch()} />
            ) : funnelChartData.length === 0 ? (
              <EmptyState icon={<Inbox className="h-5 w-5" />} title="No orders in range" />
            ) : (
              <>
                <BarChart
                  data={funnelChartData}
                  xKey="label"
                  yKey="count"
                  formatValue={(v) => fmt.number(v)}
                  colorFor={(_, i) => palette.series[i % palette.series.length]()}
                />
                <div className="mt-4 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                  {(funnel.data?.steps ?? []).slice(1).map((s) => (
                    <div
                      key={s.status}
                      className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-xs"
                    >
                      <span className="text-muted-foreground">
                        Drop to <span className="font-medium text-foreground">{statusLabel(s.status)}</span>
                      </span>
                      <span className="kpi-num font-medium">
                        {fmt.delta(s.drop_pct, { unit: 'pct' })}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Stage durations · avg</CardTitle>
            <Timer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardBody>
            {funnel.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <ul className="space-y-2.5">
                {Object.entries(funnel.data?.stage_avg_seconds ?? {}).map(([k, secs]) => (
                  <li
                    key={k}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-muted-foreground">
                      {STAGE_DURATION_LABEL[k] ?? k}
                    </span>
                    <span className="kpi-num font-medium">{fmt.duration(secs)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>SLA breaches</CardTitle>
            {sla.data ? (
              <button
                type="button"
                onClick={() => drillToOrders({ tags: ALL_SLA_TAGS.join(',') })}
                className="press rounded-full focus-ring"
                title="Show orders with any SLA breach"
              >
                <Badge tone={sla.data.total_breaches === 0 ? 'success' : 'warning'}>
                  <AnimatedNumber value={sla.data.total_breaches} duration={500} /> of{' '}
                  <AnimatedNumber value={sla.data.total_orders} duration={500} />
                </Badge>
              </button>
            ) : null}
          </CardHeader>
          <CardBody>
            {sla.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : sla.isError ? (
              <ErrorState onRetry={() => void sla.refetch()} />
            ) : slaDonut.length === 0 ? (
              <EmptyState
                icon={<Activity className="h-5 w-5" />}
                title="No SLA breaches"
                description="Every order in this range met its target time."
              />
            ) : (
              <div className="grid items-center gap-4 sm:grid-cols-[1fr,1.1fr]">
                <DonutChart
                  data={slaDonut}
                  formatValue={(v) => fmt.number(v)}
                  onSliceClick={(slice) => drillToOrders({ tags: slice.name })}
                />
                <ul className="space-y-2 text-sm">
                  {(sla.data?.breakdown ?? []).map((r, i) => (
                    <li key={r.tag}>
                      <button
                        type="button"
                        onClick={() => drillToOrders({ tags: r.tag })}
                        className="press flex w-full items-center justify-between gap-2 rounded-md px-1 py-1 text-left focus-ring hover:bg-muted/40"
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ background: palette.series[i % palette.series.length]() }}
                          />
                          <span className="font-medium">{SLA_TAG_LABELS[r.tag] ?? r.tag.replace(/_/g, ' ')}</span>
                        </span>
                        <span className="text-muted-foreground">
                          <span className="kpi-num font-medium text-foreground">{fmt.number(r.count)}</span>{' '}
                          · {fmt.pct(r.share)} · avg {fmt.duration(r.avg_late_seconds)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cancellation reasons</CardTitle>
            {cancellations.data ? (
              <Badge tone={cancellations.data.total_cancellations === 0 ? 'success' : 'danger'}>
                <AnimatedNumber value={cancellations.data.total_cancellations} duration={500} /> cancelled
              </Badge>
            ) : null}
          </CardHeader>
          <CardBody>
            {cancellations.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : cancellations.isError ? (
              <ErrorState onRetry={() => void cancellations.refetch()} />
            ) : ((cancellations.data?.results ?? cancellations.data?.top ?? []).length ?? 0) === 0 ? (
              <EmptyState
                icon={<AlertOctagon className="h-5 w-5" />}
                title="No cancellations"
              />
            ) : (
              <div className="space-y-3">
                <ul className="divide-y divide-border">
                  {(cancellations.data?.results ?? cancellations.data?.top ?? []).map((r) => (
                    <li key={r.reason}>
                      <button
                        type="button"
                        onClick={() => drillToOrders({ status: 'cancelled', cancellationReason: r.reason })}
                        className="press flex w-full items-center justify-between gap-3 rounded-md py-2 text-left text-sm focus-ring hover:bg-muted/40"
                      >
                        <span className="min-w-0 flex-1 truncate" title={r.reason}>
                          {r.reason}
                        </span>
                        <span className="text-muted-foreground">
                          <span className="kpi-num font-medium text-foreground">{fmt.number(r.count)}</span>{' '}
                          · {fmt.pct(r.share)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {cancellations.data?.pagination ? (
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      Page {fmt.number(cancellations.data.pagination.page)} of{' '}
                      {fmt.number(cancellations.data.pagination.total_pages)} ·{' '}
                      {fmt.number(cancellations.data.pagination.total_reasons)} reasons
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={cancellationPage <= 1}
                        onClick={() => setCancellationPage((p) => Math.max(1, p - 1))}
                        className="press rounded-md border border-border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        disabled={cancellationPage >= cancellations.data.pagination.total_pages}
                        onClick={() => setCancellationPage((p) => p + 1)}
                        className="press rounded-md border border-border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
      <SlaContributorsCard
        query={slaContributors}
        onStageClick={(tag) => drillToOrders({ tags: tag })}
      />
        </TabsContent>
      </Tabs>
    </>
  );
}

function SlaContributorsCard({
  query,
  onStageClick,
}: {
  query: ReturnType<typeof useOrderSlaContributors>;
  onStageClick: (tag: string) => void;
}) {
  const stages = query.data?.stages ?? [];

  return (
    <Card className="mt-4">
      <CardHeader>
        <div>
          <CardTitle>SLA breach leaders</CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Store and staff contributors by stage, ranked by breach volume and late-time impact.
          </p>
        </div>
        {query.data ? <Badge tone="neutral">{query.data.granularity} trend</Badge> : null}
      </CardHeader>
      <CardBody>
        {query.isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : stages.length === 0 || stages.every((s) => s.total_breaches === 0) ? (
          <EmptyState
            icon={<Activity className="h-5 w-5" />}
            title="No SLA contributors"
            description="No breach tags were recorded in this range."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {stages.map((stage) => {
              const topStore = stage.stores[0];
              const topStaff = stage.staff[0];
              const peak = stage.trend.reduce((max, bucket) => Math.max(max, bucket.breaches), 0);
              return (
                <button
                  key={stage.tag}
                  type="button"
                  onClick={() => onStageClick(stage.tag)}
                  className="press rounded-lg border border-border bg-surface p-3 text-left focus-ring hover:bg-muted/30"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{stage.label}</div>
                    <Badge tone={stage.total_breaches ? 'warning' : 'success'}>
                      {fmt.number(stage.total_breaches)}
                    </Badge>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {fmt.pct(stage.breach_rate)} of eligible orders
                  </div>
                  <div className="mt-3 space-y-2 text-xs">
                    <LeaderLine
                      label="Store"
                      name={topStore?.store_name}
                      count={topStore?.breaches}
                      rate={topStore?.breach_rate}
                    />
                    <LeaderLine
                      label={stage.actor_role}
                      name={topStaff?.staff_name}
                      count={topStaff?.breaches}
                      rate={topStaff?.breach_rate}
                    />
                  </div>
                  <div className="mt-3 flex h-8 items-end gap-0.5" aria-hidden>
                    {stage.trend.slice(-14).map((bucket, i) => (
                      <span
                        key={`${bucket.t ?? 'bucket'}-${i}`}
                        className="w-full rounded-t bg-primary/50"
                        style={{
                          height: `${Math.max(3, peak ? (bucket.breaches / peak) * 32 : 3)}px`,
                        }}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LeaderLine({
  label,
  name,
  count,
  rate,
}: {
  label: string;
  name?: string | null;
  count?: number;
  rate?: number;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate" title={name ?? undefined}>
          {name ?? '—'}
        </span>
        {count != null ? (
          <span className="shrink-0 text-muted-foreground">
            <span className="kpi-num text-foreground">{fmt.number(count)}</span> · {fmt.pct(rate ?? 0)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ymd(d: Date): string {
  return formatInTimeZone(d, TIMEZONE, 'yyyy-MM-dd');
}

const STATUS_FILTERS: Array<{ key: string; label: string; statuses: string }> = [
  { key: 'all', label: 'All', statuses: '' },
  { key: 'live', label: 'Live', statuses: 'created,packer_assigned,packed,billing,picked' },
  { key: 'completed', label: 'Completed', statuses: 'completed' },
  { key: 'cancelled', label: 'Cancelled', statuses: 'cancelled' },
];

function statusKeyFor(statuses: string): string {
  return STATUS_FILTERS.find((s) => s.statuses === statuses)?.key ?? 'all';
}

interface OrdersListTabProps {
  from: string;
  to: string;
  startISO: string;
  endISO: string;
  timeRangeMode: TimeRangeMode;
  storeId: number | null;
  storeIds: number[];
  statusParam: string;
  tags: string;
  cancellationReason: string;
  onClearDrilldown: () => void;
}

type OrdersView = 'list' | 'map';

function OrdersListTab({
  from,
  to,
  startISO,
  endISO,
  timeRangeMode,
  storeId,
  storeIds,
  statusParam,
  tags,
  cancellationReason,
  onClearDrilldown,
}: OrdersListTabProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setListSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusKey, setStatusKey] = useState(() => statusKeyFor(statusParam));
  const [hasRemarks, setHasRemarks] = useState<boolean | null>(null);
  const [hasFile, setHasFile] = useState<boolean | null>(null);
  const [view, setView] = useState<OrdersView>('list');
  const [expandedCancellationIds, setExpandedCancellationIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    setStatusKey(statusKeyFor(statusParam));
    setPage(1);
  }, [statusParam, tags, cancellationReason]);

  const status = statusParam || STATUS_FILTERS.find((s) => s.key === statusKey)?.statuses || '';
  const hasDrilldown = Boolean(tags || cancellationReason);
  const returnTo = `${location.pathname}${location.search}`;

  function setStatusFilter(key: string, statuses: string) {
    setStatusKey(key);
    setPage(1);
    setListSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'list');
      next.delete('tags');
      next.delete('cancellation_reason');
      if (statuses) next.set('status', statuses);
      else next.delete('status');
      return next;
    });
  }

  const query = useOrdersList({
    from, to, search, status, storeId,
    timeRangeMode,
    tags,
    cancellationReason,
    hasRemarks, hasFile,
    page, pageSize,
    sortBy: 'created_timestamp', sortOrder: 'desc',
    includeBiSlaTags: true,
  });

  // Map view shares the same primary filters (date range, store, search,
  // status chips). Only fetched when the toggle is flipped to "map" so
  // we don't spend a network round-trip on the default list view.
  const mapQuery = useOrdersPoints({
    start: startISO,
    end: endISO,
    storeId,
    storeIds,
    status: status || undefined,
    search: search || undefined,
    enabled: view === 'map',
  });

  function toggleCancellationReason(orderId: number) {
    setExpandedCancellationIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }

  const columns = useMemo<ColumnDef<OrderRow, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'Order',
        cell: ({ row }) => (
          <span className="font-mono text-xs">#{row.original.id}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const reason = row.original.cancellation_reason?.trim();
          const expanded = expandedCancellationIds.has(row.original.id);
          const shouldClamp = Boolean(reason && reason.length > 42);
          return (
            <div className="max-w-[220px] whitespace-normal">
              <Badge tone={statusTone(row.original.status)}>{statusLabel(row.original.status)}</Badge>
              {row.original.status === 'cancelled' && reason ? (
                <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                  <span className={cn(!expanded && 'line-clamp-1')} title={reason}>
                    {reason}
                  </span>
                  {shouldClamp ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCancellationReason(row.original.id);
                      }}
                      className="mt-0.5 text-primary hover:underline"
                    >
                      {expanded ? 'show less' : 'show more'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{row.original.customer ?? '—'}</div>
            <div className="text-[11px] text-muted-foreground">
              {row.original.store_name ?? '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'rider',
        header: 'Rider',
        cell: ({ row }) => <OrderRiderCell order={row.original} />,
      },
      {
        id: 'final_total',
        header: () => <div className="text-right">Total</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <div className="kpi-num text-sm font-medium">
              {fmt.npr(row.original.final_total)}
            </div>
            {row.original.coupon_code ? (
              <div className="mt-0.5 text-[11px] text-success">
                {row.original.coupon_code}
                {row.original.coupon_discount != null
                  ? ` · -${fmt.npr(row.original.coupon_discount, true)}`
                  : ''}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'qty',
        header: () => <div className="text-right">Qty</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-xs text-muted-foreground">
            {fmt.number(row.original.quantity)}
          </div>
        ),
      },
      {
        id: 'tags',
        header: 'Tags',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.original.has_remarks ? <Badge tone="warning">remarks</Badge> : null}
            {row.original.has_file ? <Badge tone="info">file</Badge> : null}
            {row.original.has_freeze_items ? <Badge tone="info">freeze</Badge> : null}
          </div>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.created_timestamp
              ? format(new Date(row.original.created_timestamp), 'MMM d, HH:mm')
              : '—'}
          </span>
        ),
      },
    ],
    [expandedCancellationIds],
  );

  const total = query.data?.total_items ?? 0;
  const rows = query.data?.results ?? [];

  const toolbar = (
    <div className="flex flex-wrap items-center gap-1.5">
      <ViewToggle value={view} onChange={setView} />
      <span className="mx-1 h-4 w-px bg-border" aria-hidden />
      {STATUS_FILTERS.map((s) => (
        <button
          key={s.key}
          type="button"
          onClick={() => setStatusFilter(s.key, s.statuses)}
          className={cn(
            'press rounded-full border px-2.5 py-1 text-[11px] focus-ring',
            statusKey === s.key
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-border bg-surface text-muted-foreground hover:text-foreground',
          )}
        >
          {s.label}
        </button>
      ))}
      {hasDrilldown ? (
        <>
          <span className="mx-1 h-4 w-px bg-border" aria-hidden />
          {tags ? (
            <Badge tone="warning">
              SLA: {tags.split(',').length > 1 ? `${tags.split(',').length} stages` : SLA_TAG_LABELS[tags] ?? tags}
            </Badge>
          ) : null}
          {cancellationReason ? (
            <Badge tone="danger">Cancel: {cancellationReason}</Badge>
          ) : null}
          <button
            type="button"
            onClick={onClearDrilldown}
            className="press rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] text-muted-foreground focus-ring hover:text-foreground"
          >
            Clear drilldown
          </button>
        </>
      ) : null}
      {view === 'list' ? (
        <>
          <span className="mx-1 h-4 w-px bg-border" aria-hidden />
          <ChipToggle label="remarks" value={hasRemarks} onChange={setHasRemarks} />
          <ChipToggle label="file" value={hasFile} onChange={setHasFile} />
        </>
      ) : null}
    </div>
  );

  if (view === 'map') {
    return (
      <OrdersMapCard
        toolbar={toolbar}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        query={mapQuery}
        onOrderClick={(id) => navigate(`/orders/${id}`, { state: { from: returnTo } })}
        onRiderClick={(riderId, riderName, storeId) =>
          openRiderOrders({
            riderId,
            riderName: riderName ?? undefined,
            ...(storeId != null ? { storeId } : {}),
          })
        }
      />
    );
  }

  return (
    <DataView<OrderRow>
      title="Orders"
      description="Drill into any row for the full timeline + items + staff + rider trail."
      data={rows}
      columns={columns}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      search={{ value: search, onChange: (v) => { setSearch(v); setPage(1); }, placeholder: 'Search by id, name, phone…' }}
      pagination={{
        page,
        pageSize,
        total,
        onPageChange: setPage,
        onPageSizeChange: (n) => { setPageSize(n); setPage(1); },
      }}
      onRowClick={(row) => navigate(`/orders/${row.id}`, { state: { from: returnTo } })}
      emptyTitle="No orders match these filters"
      emptyDescription="Try widening the range or clearing a status chip."
      toolbar={toolbar}
    />
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: OrdersView;
  onChange: (next: OrdersView) => void;
}) {
  const items: Array<{ key: OrdersView; label: string; icon: typeof List }> = [
    { key: 'list', label: 'List', icon: List },
    { key: 'map', label: 'Map', icon: MapIcon },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Toggle list or map view"
      className="inline-flex items-center rounded-full border border-border bg-surface p-0.5"
    >
      {items.map(({ key, label, icon: Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(key)}
            className={cn(
              'press inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium focus-ring transition-colors',
              active
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3 w-3" aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}

interface OrdersMapCardProps {
  toolbar: ReactNode;
  search: string;
  onSearchChange: (next: string) => void;
  query: ReturnType<typeof useOrdersPoints>;
  onOrderClick: (id: number) => void;
  onRiderClick: (riderId: number, riderName: string | null, storeId: number | null) => void;
}

function OrdersMapCard({
  toolbar,
  search,
  onSearchChange,
  query,
  onOrderClick,
  onRiderClick,
}: OrdersMapCardProps) {
  const points = query.data?.points ?? [];
  const stores = query.data?.stores ?? [];
  const totals = query.data?.totals;

  return (
    <Card>
      <CardHeader className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <CardTitle className="inline-flex items-center gap-2">
            <MapIcon className="h-4 w-4" /> Orders map
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">
            One marker per order at the customer's delivery location · color follows status.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Search by id, name, phone…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="press h-7 w-48 rounded-md border border-border bg-surface px-2 text-xs focus-ring"
          />
          {totals ? (
            <Badge tone={totals.truncated ? 'warning' : 'neutral'}>
              {fmt.number(totals.points_returned)} pts
              {totals.truncated ? ` / ${fmt.number(totals.orders_in_range_with_coords)}` : ''}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {toolbar}
        {query.isLoading ? (
          <Skeleton className="h-[520px] w-full" />
        ) : query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : points.length === 0 ? (
          <EmptyState
            icon={<MapIcon className="h-5 w-5" />}
            title="No mappable orders"
            description="No orders in this range have customer coordinates. Widen the range or pick a different store."
          />
        ) : (
          <OrdersMapView
            points={points}
            stores={stores}
            onOrderClick={onOrderClick}
            onRiderClick={onRiderClick}
            className="overflow-hidden rounded-md border border-border"
            height={520}
          />
        )}
      </CardBody>
    </Card>
  );
}

function ChipToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (next: boolean | null) => void;
}) {
  function next() {
    if (value === null) onChange(true);
    else if (value === true) onChange(false);
    else onChange(null);
  }
  return (
    <button
      type="button"
      onClick={next}
      className={cn(
        'press rounded-full border px-2.5 py-1 text-[11px] focus-ring',
        value === null && 'border-border bg-surface text-muted-foreground hover:text-foreground',
        value === true && 'border-success/40 bg-success/10 text-success',
        value === false && 'border-danger/40 bg-danger/10 text-danger',
      )}
    >
      {value === null ? label : value ? `has ${label}` : `no ${label}`}
    </button>
  );
}
